import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiChevronLeft, FiChevronDown, FiX, FiCheck, FiX as FiCross } from 'react-icons/fi';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import { Country, State, City } from 'country-state-city';
import { BASE_URL } from '../../utils/constants';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiCrossComponent = FiCross as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

interface ApiSegment {
    id: string;
    name: string;
    type: string;
    description?: string | null;
    rulesJson: any[];
}

// Interface for tag with timestamp
interface TagWithTimestamp {
    name: string;
    createdAt: number;
}

interface ContactData {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dob: string;
    gender: string;
    address: string;
    country?: string;
    city?: string;
    dedupKey: string;
    status?: string;
    lifecycleStage?: string;
    segments?: string[];
    tags?: string[];
}

// Helper function to split full name into first and last name
const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName || fullName.trim() === '') {
        return { firstName: '', lastName: '' };
    }

    const trimmedName = fullName.trim();
    const spaceIndex = trimmedName.indexOf(' ');

    if (spaceIndex === -1) {
        return { firstName: trimmedName, lastName: '' };
    }

    const firstName = trimmedName.substring(0, spaceIndex);
    const lastName = trimmedName.substring(spaceIndex + 1);

    return { firstName, lastName };
};

// Helper function to parse address string and extract address, city, and country
const parseAddressString = (addressString: string): { address: string; city: string; country: string } => {
    if (!addressString || addressString.trim() === '') {
        return { address: '', city: '', country: '' };
    }

    const parts = addressString.split(',').map(part => part.trim());
    const nonEmptyParts = parts.filter(part => part !== '' && part.toLowerCase() !== 'null');

    if (nonEmptyParts.length === 0) {
        return { address: '', city: '', country: '' };
    }

    const lastPart = nonEmptyParts[nonEmptyParts.length - 1];
    const isLastPartCountry = lastPart && (lastPart.length === 2 || lastPart.length === 3);

    const secondLastPart = nonEmptyParts.length >= 2 ? nonEmptyParts[nonEmptyParts.length - 2] : '';
    const isSecondLastPartCity = secondLastPart && secondLastPart.length > 0;

    let country = '';
    let city = '';
    let address = '';

    if (isLastPartCountry) {
        country = lastPart;

        if (isSecondLastPartCity) {
            city = secondLastPart;
            const addressParts = nonEmptyParts.slice(0, nonEmptyParts.length - 2);
            address = addressParts.length > 0 ? addressParts.join(', ') : '';
        } else {
            city = '';
            const addressParts = nonEmptyParts.slice(0, nonEmptyParts.length - 1);
            address = addressParts.length > 0 ? addressParts.join(', ') : '';
        }
    } else {
        address = nonEmptyParts.join(', ');
    }

    address = address.replace(/,\s*$/, '').trim();

    return { address, city, country };
};

const EditContact: React.FC = () => {
    const navigate = useNavigate();
    const { contactId } = useParams<{ contactId: string }>();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const [contact, setContact] = useState<ContactData | null>(null);

    // State for segments
    const [allSegments, setAllSegments] = useState<ApiSegment[]>([]);
    const [loadingSegments, setLoadingSegments] = useState(false);
    const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
    const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);

    // State for tags
    const [allTags, setAllTags] = useState<TagWithTimestamp[]>([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [tagValidationError, setTagValidationError] = useState('');

    // State for country and city
    const [countries, setCountries] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedCity, setSelectedCity] = useState<string>('');

    // Store original values to detect changes
    const [originalSegments, setOriginalSegments] = useState<string[]>([]);
    const [originalTags, setOriginalTags] = useState<string[]>([]);

    const segmentDropdownRef = useRef<HTMLDivElement>(null);
    const tagsDropdownRef = useRef<HTMLDivElement>(null);

    // ==================== FETCH COUNTRIES ====================
    useEffect(() => {
        const allCountries = Country.getAllCountries();
        setCountries(allCountries);
    }, []);

    // ==================== FETCH CITIES WHEN COUNTRY CHANGES ====================
    useEffect(() => {
        if (selectedCountry) {
            const countryCode = selectedCountry;
            const citiesOfCountry = City.getCitiesOfCountry(countryCode) || [];
            setCities(citiesOfCountry);
        } else {
            setCities([]);
        }
    }, [selectedCountry]);

    // ==================== FETCH TAGS ====================
    const fetchTags = useCallback(async () => {
        const { token, tenantId, userId } = getAuthContext();
        if (!token) return;

        setLoadingTags(true);

        try {
            const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
                method: 'GET',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const tagsData = await response.json();
                const tags = Array.isArray(tagsData) ? tagsData : [];
                const uniqueTags = tags.filter((tag: string, index: number, self: string[]) =>
                    index === self.indexOf(tag)
                );

                const tagsWithTimestamps: TagWithTimestamp[] = uniqueTags.map((tag, index) => ({
                    name: tag,
                    createdAt: Date.now() - (uniqueTags.length - index) * 1000,
                }));

                tagsWithTimestamps.sort((a, b) => b.createdAt - a.createdAt);

                setAllTags(tagsWithTimestamps);
            }
        } catch (error) {
            // console.error('❌ Failed to fetch tags:', error);
            toast.error('Failed to fetch tags. Please try again later.');
        } finally {
            setLoadingTags(false);
        }
    }, []);

    // ==================== CREATE NEW TAG ====================
    const handleCreateTag = async () => {
        const trimmedTag = newTagName.trim();

        if (!trimmedTag) {
            toast.error('Please enter a tag name');
            return;
        }

        const tagExists = allTags.some(
            existingTag => existingTag.name.toLowerCase() === trimmedTag.toLowerCase()
        );

        if (tagExists) {
            setTagValidationError('Tag already exists');
            return;
        }

        setTagValidationError('');

        const { token, tenantId, userId } = getAuthContext();
        if (!token) {
            toast.error('Authentication required');
            return;
        }

        setIsCreatingTag(true);

        try {
            const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ name: trimmedTag }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            toast.success(`Tag "${trimmedTag}" created successfully!`);

            const newTag: TagWithTimestamp = {
                name: trimmedTag,
                createdAt: Date.now(),
            };

            setAllTags(prev => [newTag, ...prev]);
            setSelectedTags(prev => [...prev, trimmedTag]);

            setIsAddingTag(false);
            setNewTagName('');

            setTimeout(() => {
                fetchTags();
            }, 2000);

        } catch (error: any) {
            // console.error('❌ Failed to create tag:', error);
            toast.error(error.message || 'Failed to create tag');
            fetchTags();
        } finally {
            setIsCreatingTag(false);
        }
    };

    // ==================== FETCH CONTACT AND SEGMENTS ====================
    useEffect(() => {
        const fetchData = async () => {
            try {
                const { token, tenantId, userId } = getAuthContext();
                if (!token) {
                    toast.error('Authentication required');
                    navigate('/audience');
                    return;
                }


                setLoadingSegments(true);
                const segmentsResponse = await fetch(`${BASE_URL}/v1/audience-insights/segments?page=0&size=100&type=STATIC`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (segmentsResponse.ok) {
                    const segmentsData = await segmentsResponse.json();
                    const segments = segmentsData.content || [];
                    const seenNames = new Set();
                    const uniqueSegments = segments.filter((seg: ApiSegment) => {
                        const duplicate = seenNames.has(seg.name);
                        seenNames.add(seg.name);
                        return !duplicate;
                    });
                    setAllSegments(uniqueSegments);
                }
                setLoadingSegments(false);

                await fetchTags();

                let contactData: any;

                if (state?.contactData) {
                    contactData = state.contactData;
                } else {
                    const contactResponse = await fetch(
                        `${BASE_URL}/v1/audience-insights/contacts/${contactId}`,
                        {
                            method: 'GET',
                            headers: {
                                'X-Tenant-Id': tenantId,
                                'X-User-Id': userId || '',
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );

                    if (!contactResponse.ok) {
                        throw new Error(`HTTP ${contactResponse.status}`);
                    }

                    contactData = await contactResponse.json();
                }

                let segmentIds: string[] = [];
                if (contactData.segments && Array.isArray(contactData.segments)) {
                    if (contactData.segments.length > 0) {
                        if (typeof contactData.segments[0] === 'string') {
                            segmentIds = contactData.segments;
                        } else {
                            segmentIds = contactData.segments.map((seg: any) => seg.id || seg);
                        }
                    }
                }

                let tags: string[] = [];
                if (contactData.tags && Array.isArray(contactData.tags)) {
                    tags = contactData.tags.filter((tag: any) => tag !== null && tag !== '');
                }

                setSelectedSegments(segmentIds);
                setOriginalSegments(segmentIds);
                setSelectedTags(tags);
                setOriginalTags(tags);

                let firstName = contactData.firstName || '';
                let lastName = contactData.lastName || '';

                if (!firstName && !lastName && contactData.name) {
                    const split = splitFullName(contactData.name);
                    firstName = split.firstName;
                    lastName = split.lastName;
                }

                let address = '';
                let country = '';
                let city = '';

                if (contactData.address) {
                    if (typeof contactData.address === 'string') {
                        const parsed = parseAddressString(contactData.address);
                        address = parsed.address;
                        city = parsed.city;
                        country = parsed.country;
                    }
                    else if (typeof contactData.address === 'object') {
                        address = contactData.address.line1 || contactData.address.address1 || '';
                        country = contactData.address.country || '';
                        city = contactData.address.city || '';
                    }
                }


                setSelectedCountry(country);
                setSelectedCity(city);

                setContact({
                    id: contactData.id,
                    firstName: firstName,
                    lastName: lastName,
                    email: contactData.email || '',
                    phone: contactData.phone || '',
                    dob: contactData.dob || '',
                    gender: contactData.gender || '',
                    address: address,
                    country: country,
                    city: city,
                    dedupKey: contactData.dedupKey || 'EMAIL',
                    status: contactData.status || 'ACTIVE',
                    lifecycleStage: contactData.lifecycleStage || 'SUBSCRIBER',
                    segments: segmentIds,
                    tags: tags,
                });
                setLoading(false);
            } catch (error) {
                // console.error('❌ Failed to fetch data:', error);
                toast.error('Failed to load contact details');
                navigate('/audience');
            }
        };

        if (contactId) {
            fetchData();
        }
    }, [contactId, navigate, state, fetchTags]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (segmentDropdownRef.current && !segmentDropdownRef.current.contains(event.target as Node)) {
                setIsSegmentDropdownOpen(false);
            }
            if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
                setIsTagsDropdownOpen(false);
                setIsAddingTag(false);
                setNewTagName('');
                setTagValidationError('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // ==================== TOGGLE SEGMENT SELECTION ====================
    const toggleSegment = (segmentId: string) => {
        setSelectedSegments(prev => {
            if (prev.includes(segmentId)) {
                return prev.filter(id => id !== segmentId);
            } else {
                return [...prev, segmentId];
            }
        });
    };

    // ==================== REMOVE SEGMENT ====================
    const removeSegment = (segmentId: string) => {
        setSelectedSegments(prev => prev.filter(id => id !== segmentId));
    };

    // ==================== TOGGLE TAG SELECTION ====================
    const toggleTag = (tagName: string) => {
        setSelectedTags(prev => {
            if (prev.includes(tagName)) {
                return prev.filter(t => t !== tagName);
            } else {
                return [...prev, tagName];
            }
        });
    };

    // ==================== REMOVE TAG ====================
    const removeTag = (tagName: string) => {
        setSelectedTags(prev => prev.filter(t => t !== tagName));
    };

    // ==================== CHECK IF SEGMENTS CHANGED ====================
    const haveSegmentsChanged = () => {
        const validSelected = selectedSegments.filter(id => allSegments.some(s => s.id === id));
        const validOriginal = originalSegments.filter(id => allSegments.some(s => s.id === id));
        if (validSelected.length !== validOriginal.length) return true;
        const sortedSelected = [...validSelected].sort();
        const sortedOriginal = [...validOriginal].sort();
        return sortedSelected.some((id, index) => id !== sortedOriginal[index]);
    };

    // ==================== CHECK IF TAGS CHANGED ====================
    const haveTagsChanged = () => {
        if (selectedTags.length !== originalTags.length) return true;
        const sortedSelected = [...selectedTags].sort();
        const sortedOriginal = [...originalTags].sort();
        return sortedSelected.some((tag, index) => tag !== sortedOriginal[index]);
    };

    // ==================== TOGGLE TAGS DROPDOWN ====================
    const toggleTagsDropdown = () => {
        const newState = !isTagsDropdownOpen;

        if (!newState) {
            setIsAddingTag(false);
            setNewTagName('');
            setTagValidationError('');
        }

        setIsTagsDropdownOpen(newState);
    };

    // ==================== VALIDATION SCHEMA ====================
    const validationSchema = Yup.object({
        firstName: Yup.string()
            .required('First name is required')
            .min(2, 'First name must be at least 2 characters'),
        lastName: Yup.string()
            .required('Last name is required')
            .min(2, 'Last name must be at least 2 characters'),
        email: Yup.string()
            .email('Invalid email format')
            .required('Email is required'),
        phone: Yup.string()
            .required('Phone number is required')
            .matches(/^\+?[0-9\s\-()]+$/, 'Invalid phone number format'),
        dob: Yup.string(),
        gender: Yup.string(),
        address: Yup.string(),
        country: Yup.string(),
        city: Yup.string(),
        dedupKey: Yup.string()
            .required('Dedup key is required')
            .oneOf(['EMAIL', 'PHONE'], 'Invalid dedup key'),
    });

    // ==================== API PAYLOAD ====================
    const handleSubmit = async (values: any) => {
        const { token, tenantId, userId } = getAuthContext();
        if (!token) {
            toast.error('Authentication required');
            return;
        }


        const payload: any = {
            firstName: values.firstName,
            lastName: values.lastName,
            dedupKey: values.dedupKey,
            email: values.email,
            phone: values.phone,
            dob: values.dob || '',
            gender: values.gender || '',
            status: contact?.status || 'ACTIVE',
            lifecycleStage: contact?.lifecycleStage || 'SUBSCRIBER',
            tags: selectedTags,
            segments: selectedSegments.filter(id => allSegments.some(s => s.id === id)),
        };

        if (values.address?.trim() || selectedCountry || selectedCity) {
            payload.address = {
                line1: values.address?.trim() || '',
                city: selectedCity || '',
                country: selectedCountry || '',
            };
        } else {
            payload.address = null;
        }


        try {
            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/contacts/${contactId}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            toast.success('Contact updated successfully!');
            setTimeout(() => navigate('/audience'), 1500);
        } catch (error: any) {
            // console.error('❌ API Error:', error);
            toast.error(error.message || 'Failed to update contact');
        }
    };

    if (loading) {
        return (
            <div className='p-2'>
                <div className='flex items-center justify-center h-64'>
                    <div className='text-gray-500'>Loading contact details...</div>
                </div>
            </div>
        );
    }

    if (!contact) {
        return (
            <div className='p-2'>
                <div className='flex items-center justify-center h-64'>
                    <div className='text-gray-500'>Contact not found</div>
                </div>
            </div>
        );
    }

    return (
        <div className='p-2'>
            <button
                className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
                onClick={() => navigate('/audience')}
            >
                <FiChevronLeftComponent className='mr-1' /> Back to Audience
            </button>

            <div className='flex justify-between items-center mb-6'>
                <div>
                    <h2 className='text-2xl font-bold text-[#3F5265]'>Edit Contact</h2>
                    <p className='text-[#657584] text-[16px]'>Update contact information</p>
                </div>
            </div>

            <Formik
                initialValues={{
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email: contact.email,
                    phone: contact.phone,
                    dob: contact.dob || '',
                    gender: contact.gender || '',
                    address: contact.address || '',
                    country: contact.country || '',
                    city: contact.city || '',
                    dedupKey: contact.dedupKey || 'EMAIL',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ isValid, dirty, isSubmitting, values, setFieldValue }) => {
                    // Only count segments that actually exist in allSegments
                    const validSelectedSegments = selectedSegments.filter(id =>
                        allSegments.some(s => s.id === id)
                    );

                    const hasFormChanges = dirty;
                    const hasSegmentChanges = haveSegmentsChanged();
                    const hasTagChanges = haveTagsChanged();
                    const hasAnyChanges = hasFormChanges || hasSegmentChanges || hasTagChanges;

                    const isFormValid = isValid;
                    const isButtonEnabled = isFormValid && hasAnyChanges && !isSubmitting;

                    return (
                        <Form className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
                            <div className='grid grid-cols-2 gap-4'>
                                {/* First Name */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                        FIRST NAME <span className='text-red-500'>*</span>
                                    </label>
                                    <Field
                                        name="firstName"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                    />
                                    <ErrorMessage name="firstName" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                        LAST NAME <span className='text-red-500'>*</span>
                                    </label>
                                    <Field
                                        name="lastName"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                    />
                                    <ErrorMessage name="lastName" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Email - Made Read-Only */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                        EMAIL <span className='text-red-500'>*</span>
                                    </label>
                                    <Field
                                        type="email"
                                        name="email"
                                        readOnly
                                        className='w-full border px-3 py-2 rounded bg-gray-100 text-gray-600 cursor-not-allowed focus:outline-none'
                                        style={{ backgroundColor: '#f3f4f6' }}
                                    />
                                    <ErrorMessage name="email" component="div" className='text-red-500 text-xs mt-1' />
                                    <p className='text-xs text-gray-400 mt-1'>Email cannot be changed</p>
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                        PHONE <span className='text-red-500'>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="phone"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                        placeholder="+1-555-123-4567"
                                    />
                                    <ErrorMessage name="phone" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* DOB */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>DOB</label>
                                    <Field
                                        type="date"
                                        name="dob"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                    />
                                    <ErrorMessage name="dob" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Gender */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>GENDER</label>
                                    <Field
                                        as="select"
                                        name="gender"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                    >
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </Field>
                                    <ErrorMessage name="gender" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Address */}
                                <div className='col-span-2'>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>ADDRESS</label>
                                    <Field
                                        name="address"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                        placeholder="123 Main Street"
                                        value={values.address || ''}
                                        onChange={(e: any) => {
                                            setFieldValue('address', e.target.value);
                                        }}
                                    />
                                    <ErrorMessage name="address" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Country and City */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>COUNTRY</label>
                                    <select
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                        value={selectedCountry || ''}
                                        onChange={(e) => {
                                            const countryCode = e.target.value;
                                            setCities([]);
                                            setSelectedCity('');
                                            setSelectedCountry(countryCode);
                                            setFieldValue('country', countryCode);
                                            setFieldValue('city', '');
                                        }}
                                    >
                                        <option value="">Select Country</option>
                                        {countries.map((country) => (
                                            <option key={country.isoCode} value={country.isoCode}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>CITY</label>
                                    <select
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                        value={selectedCity || ''}
                                        onChange={(e) => {
                                            const cityName = e.target.value;
                                            setSelectedCity(cityName);
                                            setFieldValue('city', cityName);
                                        }}
                                        disabled={!selectedCountry}
                                    >
                                        <option value="">Select City</option>
                                        {cities.map((city) => (
                                            <option key={city.name} value={city.name}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Dedup Key - Unique ID */}
                                <div>
                                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                        UNIQUE ID <span className='text-red-500'>*</span>
                                    </label>
                                    <Field
                                        as="select"
                                        name="dedupKey"
                                        className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                                    >
                                        <option value="EMAIL">Email</option>
                                        <option value="PHONE">Phone</option>
                                    </Field>
                                    <ErrorMessage name="dedupKey" component="div" className='text-red-500 text-xs mt-1' />
                                </div>

                                {/* Segments and Tags side by side */}
                                <div className='col-span-2'>
                                    <div className='grid grid-cols-2 gap-4'>
                                        {/* Multi-Select Segments */}
                                        <div>
                                            <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                                STATIC SEGMENTS <span className='text-gray-400 text-xs'>(Optional)</span>
                                            </label>
                                            <p className='text-xs text-gray-400 mb-2'>
                                                Select static segments to associate
                                            </p>

                                            {validSelectedSegments.length > 0 && (
                                                <div className='flex flex-wrap gap-2 mb-2'>
                                                    {validSelectedSegments.map(segId => {
                                                        const seg = allSegments.find(s => s.id === segId);
                                                        if (!seg) return null;

                                                        return (
                                                            <span
                                                                key={segId}
                                                                className='inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs'
                                                            >
                                                                {seg.name}
                                                                <button
                                                                    type='button'
                                                                    onClick={() => removeSegment(segId)}
                                                                    className='hover:text-blue-600'
                                                                >
                                                                    <FiXComponent size={14} />
                                                                </button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className='relative' ref={segmentDropdownRef}>
                                                <button
                                                    type='button'
                                                    onClick={() => setIsSegmentDropdownOpen(!isSegmentDropdownOpen)}
                                                    className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center'
                                                >
                                                    <span className='text-gray-600 text-sm'>
                                                        {validSelectedSegments.length > 0
                                                            ? `${validSelectedSegments.length} segment${validSelectedSegments.length > 1 ? 's' : ''} selected`
                                                            : 'Select static segments...'}
                                                    </span>
                                                    <FiChevronDownComponent className={`transition-transform ${isSegmentDropdownOpen ? 'rotate-180' : ''}`} />
                                                </button>

                                                {isSegmentDropdownOpen && (
                                                    <div className='absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto'>
                                                        {loadingSegments ? (
                                                            <div className='p-3 text-center text-gray-500'>Loading segments...</div>
                                                        ) : allSegments.length === 0 ? (
                                                            <div className='p-3 text-center text-gray-500'>No static segments available</div>
                                                        ) : (
                                                            allSegments.map(seg => {
                                                                const isChecked = selectedSegments.includes(seg.id);
                                                                return (
                                                                    <label key={seg.id} className='flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer'>
                                                                        <input
                                                                            type='checkbox'
                                                                            checked={isChecked}
                                                                            onChange={() => toggleSegment(seg.id)}
                                                                            className='mr-2 w-4 h-4 text-teal-600 rounded'
                                                                        />
                                                                        <span className='text-sm'>{seg.name}</span>
                                                                        <span className='ml-2 text-xs text-gray-400'>
                                                                            ({seg.type})
                                                                        </span>
                                                                        {isChecked && (
                                                                            <span className='ml-auto text-xs text-blue-600'>✓</span>
                                                                        )}
                                                                    </label>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <p className='text-xs text-gray-400 mt-1'>
                                                {validSelectedSegments.length === 0 && (
                                                    <span className='text-gray-500'>No segments selected</span>
                                                )}
                                                {validSelectedSegments.length > 0 && (
                                                    <span className='text-blue-600'>
                                                        {validSelectedSegments.length} segment{validSelectedSegments.length > 1 ? 's' : ''} selected
                                                    </span>
                                                )}
                                            </p>
                                        </div>

                                        {/* Multi-Select Tags with Add Tag feature */}
                                        <div>
                                            <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                                                TAGS <span className='text-gray-400 text-xs'>(Optional)</span>
                                            </label>
                                            <p className='text-xs text-gray-400 mb-2'>
                                                Select tags to associate
                                            </p>

                                            {selectedTags.length > 0 && (
                                                <div className='flex flex-wrap gap-2 mb-2'>
                                                    {selectedTags.map(tag => (
                                                        <span key={tag} className='inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs'>
                                                            {tag}
                                                            <button
                                                                type='button'
                                                                onClick={() => removeTag(tag)}
                                                                className='hover:text-purple-600'
                                                            >
                                                                <FiXComponent size={14} />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className='relative' ref={tagsDropdownRef}>
                                                <button
                                                    type='button'
                                                    onClick={toggleTagsDropdown}
                                                    className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center'
                                                >
                                                    <span className='text-gray-600 text-sm'>
                                                        {selectedTags.length > 0
                                                            ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`
                                                            : 'Select tags...'}
                                                    </span>
                                                    <FiChevronDownComponent className={`transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
                                                </button>

                                                {isTagsDropdownOpen && (
                                                    <div className='absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto'>
                                                        {loadingTags ? (
                                                            <div className='p-3 text-center text-gray-500'>Loading tags...</div>
                                                        ) : (
                                                            <>
                                                                {!isAddingTag ? (
                                                                    <div
                                                                        className='flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100'
                                                                        onClick={() => {
                                                                            setIsAddingTag(true);
                                                                            setTagValidationError('');
                                                                        }}
                                                                    >
                                                                        <span className='text-blue-600 font-medium text-sm flex items-center gap-2'>
                                                                            <span className='text-blue-500 text-lg'>+</span> Add Tag
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div className='px-3 py-2 bg-blue-50 border-b border-blue-200'>
                                                                        <div className='flex items-center gap-2'>
                                                                            <input
                                                                                type='text'
                                                                                value={newTagName}
                                                                                onChange={(e) => {
                                                                                    setNewTagName(e.target.value);
                                                                                    if (tagValidationError) {
                                                                                        setTagValidationError('');
                                                                                    }
                                                                                }}
                                                                                placeholder='Enter tag name...'
                                                                                className={`flex-1 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${tagValidationError ? 'border-red-500' : 'border-blue-300'
                                                                                    }`}
                                                                                autoFocus
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        handleCreateTag();
                                                                                    }
                                                                                    if (e.key === 'Escape') {
                                                                                        setIsAddingTag(false);
                                                                                        setNewTagName('');
                                                                                        setTagValidationError('');
                                                                                    }
                                                                                }}
                                                                                disabled={isCreatingTag}
                                                                            />
                                                                            <button
                                                                                type='button'
                                                                                onClick={handleCreateTag}
                                                                                disabled={isCreatingTag || !newTagName.trim()}
                                                                                className={`p-1 rounded hover:bg-green-100 ${isCreatingTag || !newTagName.trim() ? 'opacity-50 cursor-not-allowed' : ''
                                                                                    }`}
                                                                                title='Create tag'
                                                                            >
                                                                                <FiCheckComponent size={18} className='text-green-600' />
                                                                            </button>
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => {
                                                                                    setIsAddingTag(false);
                                                                                    setNewTagName('');
                                                                                    setTagValidationError('');
                                                                                }}
                                                                                className='p-1 rounded hover:bg-red-100'
                                                                                title='Cancel'
                                                                            >
                                                                                <FiCrossComponent size={18} className='text-red-600' />
                                                                            </button>
                                                                        </div>

                                                                        {tagValidationError && (
                                                                            <div className='text-red-500 text-xs mt-1 flex items-center gap-1'>
                                                                                <span>⚠️</span> {tagValidationError}
                                                                            </div>
                                                                        )}

                                                                        {isCreatingTag && (
                                                                            <div className='text-xs text-gray-500 mt-1'>Creating tag...</div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {allTags.length === 0 && !isAddingTag ? (
                                                                    <div className='p-3 text-center text-gray-500'>No tags available</div>
                                                                ) : (
                                                                    allTags.map(tagObj => (
                                                                        <label key={tagObj.name} className='flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer'>
                                                                            <input
                                                                                type='checkbox'
                                                                                checked={selectedTags.includes(tagObj.name)}
                                                                                onChange={() => toggleTag(tagObj.name)}
                                                                                className='mr-2 w-4 h-4 text-teal-600 rounded'
                                                                            />
                                                                            <span className='text-sm'>{tagObj.name}</span>
                                                                            {selectedTags.includes(tagObj.name) && (
                                                                                <span className='ml-auto text-xs text-purple-600'>✓</span>
                                                                            )}
                                                                        </label>
                                                                    ))
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <p className='text-xs text-gray-400 mt-1'>
                                                {selectedTags.length === 0 && (
                                                    <span className='text-gray-500'>No tags selected</span>
                                                )}
                                                {selectedTags.length > 0 && (
                                                    <span className='text-purple-600'>
                                                        {selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''} selected
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className='col-span-2 flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200'>
                                    <button
                                        type='button'
                                        onClick={() => navigate('/audience')}
                                        className='px-6 py-2 border rounded text-gray-600 hover:bg-gray-50'
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='submit'
                                        disabled={!isButtonEnabled}
                                        className={`px-6 py-2 rounded text-white font-medium ${isButtonEnabled
                                            ? 'bg-[#00899E] hover:bg-[#007A8A]'
                                            : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update'}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>

            <ToastContainer position='top-right' autoClose={3000} />
        </div>
    );
};

export default EditContact;